// 通用提现视图组件（支持 EVM 和 MPC 钱包）
import React, { useState, useCallback, useMemo } from 'react'
import { Selector as ChainTokenSelector } from '@/components/shared/components/selector'
import { WithdrawWalletView } from './WalletView'
import BigNumber from 'bignumber.js'
import { ChainInfo } from '@/types'
import { useMulticallBalance } from '../../../Deposit/src/hooks/useMulticallBalance'
import { isEthChain } from '../constants'
import { setWithdrawLoading } from '../WithdrawManager'
// i18n
import { useTranslation, usePoolBalance } from '@/hooks'
// components
import { LoadingSpinner } from '@/components/LoadingSpinner'

interface WithdrawViewProps {
  address: string
  chains: ChainInfo[]
  defaultChainId?: number
  defaultTokenAddress?: string
  defaultAmount?: string

  walletType: 'mpc' | 'evm'

  metadata?: any
  currentEquity?: number
  platformBalance?: string // 用户在平台上的当前余额
  fee?: string // 手续费金额
  isLoading?: boolean // loading 状态

  // Wagmi hooks - 由外部项目传入
  wagmiHooks?: {
    useBalance: any
    useReadContract: any
    useAccount: any
  }

  // 回调
  onWithdraw?: (withdrawInfo: {
    amount: string
    chainId: number
    tokenAddress: string
    tokenSymbol: string
    receiverAddress?: string // MPC 钱包需要接收地址
  }) => void
  onClose: () => void
  onChainChange?: (chainId: number) => void
  onTokenChange?: (tokenSymbol: string) => void
  onAmountChanged?: (amount: string) => void
}

export const WithdrawView: React.FC<WithdrawViewProps> = ({
  address,
  walletType,
  chains,
  defaultChainId: _defaultChainId,
  defaultTokenAddress: _defaultTokenAddress,
  defaultAmount = '',
  metadata: _metadata,
  currentEquity,
  platformBalance = '0', // 默认值为 '0'
  fee = '0.00', // 默认手续费为 '0.00'
  isLoading = false, // loading 状态
  wagmiHooks,
  onWithdraw,
  onClose: _onClose,
  onChainChange,
  onTokenChange,
  onAmountChanged,
}) => {
  // i18n
  const { t } = useTranslation()

  // 从 chains 中挑选 RPC；优先使用主网ID，其次测试网ID；优先使用 appRpcUrl，其次 rpcUrl
  const bscRpcUrl = useMemo(() => {
    const preferIds = [56, 97]
    const pick = (id: number) => chains?.find(c => Number(c?.chainId) === id)
    const c = pick(preferIds[0]) || pick(preferIds[1])
    return c?.rpcUrl
  }, [chains])

  const arbitrumRpcUrl = useMemo(() => {
    const preferIds = [42161, 421614]
    const pick = (id: number) => chains?.find(c => Number(c?.chainId) === id)
    const c = pick(preferIds[0]) || pick(preferIds[1])
    return c?.rpcUrl
  }, [chains])
  // Prefer USDC as default token; if not specified by props, pick a chain that supports USDC
  const defaultSelection = useMemo(() => {
    const preferSymbol = 'USDC'
    if (!chains || chains.length === 0) return { chainId: 1, tokenSymbol: '' }

    const hasUSDC = (c: ChainInfo) =>
      Array.isArray(c.tokenList) &&
      c.tokenList.some(
        t => (t.token || t.symbol || '').toUpperCase() === preferSymbol
      )

    // Force USDC priority: always pick the first chain that supports USDC; fallback to first chain
    const baseChain = chains.find(hasUSDC) || chains[0]

    if (
      !baseChain ||
      !Array.isArray(baseChain.tokenList) ||
      baseChain.tokenList.length === 0
    ) {
      return { chainId: baseChain?.chainId || 1, tokenSymbol: '' }
    }

    // Token: force USDC if available on the chosen chain; otherwise fallback to first token
    const usdcTok = baseChain.tokenList.find(
      t => (t.token || t.symbol || '').toUpperCase() === preferSymbol
    )
    const tokenSymbol =
      usdcTok?.token ||
      usdcTok?.symbol ||
      baseChain.tokenList[0]?.token ||
      baseChain.tokenList[0]?.symbol ||
      ''

    return { chainId: baseChain.chainId, tokenSymbol }
  }, [chains])

  const { ARB_USDC_BALANCE, ARB_USDT_BALANCE, BNB_USDT_BALANCE } =
    usePoolBalance({ bscRpcUrl, arbitrumRpcUrl })

  // 简单的状态管理 - 添加空值检查
  const [selectedChainId, setSelectedChainId] = useState(
    () => defaultSelection.chainId
  )

  const [selectedTokenSymbol, setSelectedTokenSymbol] = useState(
    () => defaultSelection.tokenSymbol
  )

  const [withdrawAmount, setWithdrawAmount] = useState(defaultAmount)
  const [receiverAddress, setReceiverAddress] = useState('') // MPC 钱包的接收地址

  // 包装的回调函数，同时处理内部状态和外部回调
  const handleChainChange = useCallback(
    (chainId: number) => {
      setSelectedChainId(chainId)
      onChainChange?.(chainId)
    },
    [onChainChange]
  )

  const handleTokenChange = useCallback(
    (tokenSymbol: string) => {
      setSelectedTokenSymbol(tokenSymbol)
      onTokenChange?.(tokenSymbol)
    },
    [onTokenChange]
  )

  // 使用 ref 来跟踪是否是初始加载
  const isInitialMount = React.useRef(true)
  const prevTokenSymbol = React.useRef(selectedTokenSymbol)
  const prevChainId = React.useRef(selectedChainId)

  // 监听币种和链切换，清空输入金额
  React.useEffect(() => {
    if (isInitialMount.current) {
      // 初始加载时，只更新 ref，不清空输入
      isInitialMount.current = false
      prevTokenSymbol.current = selectedTokenSymbol
      prevChainId.current = selectedChainId
      return
    }

    // 检查是否真的发生了切换
    if (
      prevTokenSymbol.current !== selectedTokenSymbol ||
      prevChainId.current !== selectedChainId
    ) {
      // 当币种或链切换时，清空输入框
      // 这样避免不同币种/链之间的余额混淆
      setWithdrawAmount('')

      // 更新 ref
      prevTokenSymbol.current = selectedTokenSymbol
      prevChainId.current = selectedChainId
    }
  }, [selectedTokenSymbol, selectedChainId])

  // 派生值 - 添加空值检查
  const currentActiveChain =
    chains && chains.length > 0
      ? chains.find(c => c.chainId === selectedChainId) || chains[0]
      : null

  const selectedToken = currentActiveChain?.tokenList?.find(
    t => t.token === selectedTokenSymbol
  )

  const { multicallResult, queryComponents } = useMulticallBalance({
    account: address,
    chainId: selectedChainId,
    contractAddress: undefined, // 暂时不传入合约地址
    tokens: currentActiveChain?.tokenList || [],
    wagmiHooks,
  })

  // 获取当前选中代币的余额
  const currentTokenBalance = useMemo(() => {
    if (!selectedToken) return undefined

    // 如果是基础代币（ETH等）
    if (
      !selectedToken.tokenAddress ||
      selectedToken.tokenAddress.toLowerCase() ===
        '0xeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeeee'
    ) {
      return multicallResult.baseTokenBalance.formatted
    }

    // 如果是 ERC20 代币
    const tokenData = multicallResult.combinedData[selectedToken.tokenAddress]
    return tokenData?.balance?.formatted || '0'
  }, [selectedToken, multicallResult])

  // 格式化为不含小数的字符串
  const toIntegerStr = (v?: string) => {
    if (!v) return '0'
    const i = v.indexOf('.')
    return i >= 0 ? v.slice(0, i) : v
  }

  // 当前链的资金池余额展示（根据链与代币判断）
  const currentChainPoolBalance = useMemo(() => {
    const token = (selectedTokenSymbol || '').toUpperCase()
    const id = Number(selectedChainId)

    // 预留库存：展示为（实际余额 - 10000），下限 0
    const minusReserve = (v?: string) => {
      const intStr = toIntegerStr(v)
      try {
        const bn = new BigNumber(intStr || '0')
        const adjusted = bn.minus(10000)
        return adjusted.isGreaterThan(0) ? adjusted.toFixed(0) : '0'
      } catch {
        return '0'
      }
    }

    // ETH 链：无限
    if (isEthChain(id)) return '∞'

    // Arbitrum（主网/测试网）
    if (id === 42161 || id === 421614) {
      if (token === 'USDT') return minusReserve(ARB_USDT_BALANCE)
      if (token === 'USDC') return minusReserve(ARB_USDC_BALANCE)
    }

    // BNB 链
    if (id === 56 || id === 97) {
      if (token === 'USDT') return minusReserve(BNB_USDT_BALANCE)
    }

    // 其他情况：返回 0（或可根据需要调整为空串）
    return '0'
  }, [
    selectedChainId,
    selectedTokenSymbol,
    ARB_USDC_BALANCE,
    ARB_USDT_BALANCE,
    BNB_USDT_BALANCE,
  ])

  // 是否显示“超过资金池余额”的提示文案（仅非 ETH 链）
  const showPoolBalanceInsufficientTip = useMemo(() => {
    if (isEthChain(selectedChainId)) return false
    try {
      const amountBn = new BigNumber(withdrawAmount || '0')
      const poolBn = new BigNumber((currentChainPoolBalance || '0').toString())
      return amountBn.isGreaterThan(poolBn)
    } catch {
      return false
    }
  }, [selectedChainId, withdrawAmount, currentChainPoolBalance])

  // 提现按钮状态逻辑

  // 构造链ID => 资金池余额的映射（根据当前所选代币规则与链）
  const poolBalanceMap = useMemo(() => {
    const map: Record<number, string> = {}
    const token = (selectedTokenSymbol || '').toUpperCase()

    const minusReserve = (v?: string) => {
      const intStr = toIntegerStr(v)
      try {
        const bn = new BigNumber(intStr || '0')
        const adjusted = bn.minus(10000)
        return adjusted.isGreaterThan(0) ? adjusted.toFixed(0) : '0'
      } catch {
        return '0'
      }
    }

    ;(chains || []).forEach(c => {
      const id = Number(c?.chainId)
      if (!id) return
      if (isEthChain(id)) {
        map[id] = '∞'
        return
      }
      if (id === 42161 || id === 421614) {
        if (token === 'USDT') map[id] = minusReserve(ARB_USDT_BALANCE)
        else if (token === 'USDC') map[id] = minusReserve(ARB_USDC_BALANCE)
        else map[id] = '0'
        return
      }
      if (id === 56 || id === 97) {
        if (token === 'USDT') map[id] = minusReserve(BNB_USDT_BALANCE)
        else map[id] = '0'
        return
      }
      map[id] = '0'
    })

    return map
  }, [
    chains,
    selectedTokenSymbol,
    ARB_USDC_BALANCE,
    ARB_USDT_BALANCE,
    BNB_USDT_BALANCE,
  ])

  const withdrawButtonState = useMemo(() => {
    const amount = new BigNumber(withdrawAmount || '0')
    const feeAmount = new BigNumber(fee || '0')
    const isEth = isEthChain(selectedChainId)

    // 非 ETH 链：校验是否超过资金池可用上限（currentChainPoolBalance 已做去小数与预留库存）
    let overPoolLimit = false
    if (!isEth) {
      try {
        const poolBn = new BigNumber(
          (currentChainPoolBalance || '0').toString()
        )
        overPoolLimit = amount.isGreaterThan(poolBn)
      } catch {
        overPoolLimit = false
      }
    }

    if (
      !withdrawAmount ||
      amount.isZero() ||
      (!isEth && amount.isLessThan(feeAmount)) ||
      (!isEth && overPoolLimit)
    ) {
      return {
        text: t('withdraw.confirmWithdraw'),
        disabled: true,
      }
    }

    // MPC 钱包需要检查接收地址 (但不包括 ETH 相关链)
    if (walletType === 'mpc' && !isEth && !receiverAddress.trim()) {
      return {
        text: t('withdraw.invalidReceiverAddress'),
        disabled: true,
      }
    }

    return {
      text: t('withdraw.confirmWithdraw'),
      disabled: false,
    }
  }, [
    t,
    selectedChainId,
    withdrawAmount,
    walletType,
    receiverAddress,
    fee,
    currentChainPoolBalance,
  ])

  // 处理提现
  const handleWithdraw = useCallback(async () => {
    // 设置 loading 状态
    setWithdrawLoading(true)

    try {
      // 传递必要的提现信息
      if (onWithdraw) {
        const amount = new BigNumber(withdrawAmount || '0')

        const withdrawInfo: any = {
          amount: amount.toString(),
          chainId: selectedChainId,
          tokenAddress: selectedToken?.tokenAddress || '',
          tokenSymbol: selectedTokenSymbol,
        }

        // MPC 钱包需要包含接收地址 (但不包括 ETH 相关链)
        if (walletType === 'mpc' && !isEthChain(selectedChainId)) {
          withdrawInfo.receiverAddress = receiverAddress
        }

        onWithdraw(withdrawInfo)
      }
    } catch (error) {
      setWithdrawLoading(false)
    }
  }, [
    onWithdraw,
    withdrawAmount,
    selectedChainId,
    selectedToken?.tokenAddress,
    selectedTokenSymbol,
    walletType,
    receiverAddress,
  ])

  // 如果没有有效的链数据，显示错误信息
  if (!chains || chains.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">No chains available</p>
      </div>
    )
  }

  // 如果没有当前活跃链，显示加载状态
  if (!currentActiveChain) {
    return (
      <div className="text-center py-8">
        <p className="text-gray-400">Loading...</p>
      </div>
    )
  }

  return (
    <>
      {/* 渲染代币查询组件（逻辑组件，不显示UI） */}
      {queryComponents}

      {/* 链和代币选择器 */}
      <ChainTokenSelector
        currentActiveChain={currentActiveChain}
        chainList={chains}
        chainPoolBalances={poolBalanceMap}
        onChainChange={handleChainChange}
        curSelectToken={selectedTokenSymbol}
        onTokenChange={handleTokenChange}
        walletType="EVM"
        txType="withdraw"
      />

      <div className="p-3 my-4 bg-fill-input-primary rounded-lg flex items-center justify-between h-[38px]">
        <div className="text-text-secondary text-sm">
          {isEthChain(selectedChainId)
            ? t('withdraw.poolBalance')
            : t('withdraw.crossChainPoolBalance')}
        </div>
        <div className="text-text-primary text-sm font-medium">
          {currentChainPoolBalance} {selectedTokenSymbol}
        </div>
      </div>

      {/* 提现钱包视图 */}
      <WithdrawWalletView
        withdrawAmount={withdrawAmount}
        curSelectToken={selectedTokenSymbol}
        tokenInfo={selectedToken}
        curSelectTokenData={{
          balance: selectedToken?.balance,
          allowance: selectedToken?.allowance,
        }}
        currentActiveChain={currentActiveChain}
        currentEquity={currentEquity || 0}
        amount={withdrawAmount}
        decimals={selectedToken?.decimals || 6}
        tokenBalance={currentTokenBalance}
        platformBalance={platformBalance} // 传递平台余额
        fee={fee} // 传递手续费
        walletType={walletType} // 传递钱包类型
        receiverAddress={receiverAddress} // 传递接收地址
        onWithdrawAmountChange={setWithdrawAmount}
        onReceiverAddressChange={setReceiverAddress} // 传递接收地址变化回调
        onAmountChanged={onAmountChanged} // 传递金额失去焦点回调
        showPoolBalanceInsufficientTip={showPoolBalanceInsufficientTip}
        poolBalanceMap={poolBalanceMap}
      />

      {/* 提现按钮 */}
      <button
        className={`w-full h-[38px] text-sm rounded-xl font-medium transition-colors flex items-center justify-center gap-2 ${
          withdrawButtonState.disabled || isLoading
            ? 'bg-text-disabled text-text-secondary cursor-not-allowed'
            : 'bg-fill-btn-primary text-text-on-sec-btn'
        }`}
        onClick={handleWithdraw}
        disabled={withdrawButtonState.disabled || isLoading}
      >
        {isLoading && <LoadingSpinner size="xs" />}
        {withdrawButtonState.text}
      </button>
    </>
  )
}
